import type { GameFeedback } from '../app/game-feedback';
import type { GameEvent, GamePhase, GameSnapshot, Lane, PlayerAction } from '../core/types';

interface ToneOptions {
  readonly type: OscillatorType;
  readonly startFrequency: number;
  readonly endFrequency: number;
  readonly duration: number;
  readonly volume: number;
  readonly pan?: number;
  readonly delay?: number;
}

interface NoiseOptions {
  readonly filterType: BiquadFilterType;
  readonly startFrequency: number;
  readonly endFrequency: number;
  readonly duration: number;
  readonly volume: number;
  readonly pan?: number;
}

const MASTER_VOLUME = 0.28;

export class ActionAudio implements GameFeedback {
  private context: AudioContext | undefined;
  private master: GainNode | undefined;
  private noiseBuffer: AudioBuffer | undefined;
  private muted = false;
  private initialized = false;
  private previousAction: PlayerAction = 'grounded';
  private previousLane: Lane = 0;
  private previousPhase: GamePhase = 'ready';
  private previousElapsed = 0;
  private previousCountdown = 0;

  public constructor() {
    window.addEventListener('pointerdown', this.unlockFromGesture, true);
    window.addEventListener('keydown', this.unlockFromGesture, true);
  }

  public unlock(): void {
    if (this.muted) return;
    const context = this.ensureContext();
    if (context?.state === 'suspended') void context.resume().catch(() => undefined);
  }

  public update(snapshot: Readonly<GameSnapshot>): void {
    if (snapshot.elapsed < this.previousElapsed) this.initialized = false;

    if (!this.initialized) {
      this.previousAction = snapshot.player.action;
      this.previousLane = snapshot.player.lane;
      this.previousPhase = snapshot.phase;
      this.previousCountdown = Math.ceil(snapshot.countdownRemaining);
      this.previousElapsed = snapshot.elapsed;
      this.initialized = true;
      return;
    }

    if (snapshot.phase === 'countdown') {
      const countdown = Math.max(1, Math.ceil(snapshot.countdownRemaining));
      if (this.previousPhase !== 'countdown' || countdown !== this.previousCountdown) {
        this.playCountdown(countdown);
      }
      this.previousCountdown = countdown;
    }
    if (this.previousPhase === 'countdown' && snapshot.phase === 'running') this.playStart();

    if (snapshot.phase === 'running') {
      if (snapshot.player.lane !== this.previousLane) {
        this.playLaneChange(snapshot.player.lane > this.previousLane ? 1 : -1);
      }
      if (this.previousAction !== 'jumping' && snapshot.player.action === 'jumping') {
        this.playJump();
      }
      if (this.previousAction === 'jumping' && snapshot.player.action === 'grounded') {
        this.playLanding();
      }
      if (this.previousAction !== 'sliding' && snapshot.player.action === 'sliding') {
        this.playSlide();
      }
    }

    this.previousAction = snapshot.player.action;
    this.previousLane = snapshot.player.lane;
    this.previousPhase = snapshot.phase;
    this.previousElapsed = snapshot.elapsed;
  }

  public handleEvents(events: readonly GameEvent[]): void {
    if (events.some((event) => event.type === 'collision')) this.playCollision();
  }

  public isMuted(): boolean {
    return this.muted;
  }

  public setMuted(muted: boolean): void {
    this.muted = muted;
    if (!muted) this.unlock();
    if (this.context !== undefined && this.master !== undefined) {
      this.master.gain.setTargetAtTime(muted ? 0 : MASTER_VOLUME, this.context.currentTime, 0.015);
    }
  }

  public dispose(): void {
    window.removeEventListener('pointerdown', this.unlockFromGesture, true);
    window.removeEventListener('keydown', this.unlockFromGesture, true);
    if (this.context !== undefined) void this.context.close().catch(() => undefined);
  }

  private readonly unlockFromGesture = (): void => {
    this.unlock();
  };

  private ensureContext(): AudioContext | undefined {
    if (this.context !== undefined) return this.context;
    if (typeof window.AudioContext === 'undefined') return undefined;

    const context = new AudioContext();
    const master = context.createGain();
    const compressor = context.createDynamicsCompressor();
    master.gain.value = this.muted ? 0 : MASTER_VOLUME;
    compressor.threshold.value = -20;
    compressor.knee.value = 12;
    compressor.ratio.value = 6;
    compressor.attack.value = 0.002;
    compressor.release.value = 0.16;
    master.connect(compressor);
    compressor.connect(context.destination);

    this.context = context;
    this.master = master;
    this.noiseBuffer = this.createNoiseBuffer(context);
    return context;
  }

  private createNoiseBuffer(context: AudioContext): AudioBuffer {
    const buffer = context.createBuffer(1, context.sampleRate, context.sampleRate);
    const data = buffer.getChannelData(0);
    let state = 0x6d2b79f5;
    for (let index = 0; index < data.length; index += 1) {
      state = (Math.imul(state, 1_664_525) + 1_013_904_223) >>> 0;
      data[index] = (state / 0x1_0000_0000) * 2 - 1;
    }
    return buffer;
  }

  private playCountdown(value: number): void {
    const frequency = value === 1 ? 430 : 320;
    this.playTone({
      type: 'square',
      startFrequency: frequency,
      endFrequency: frequency * 0.92,
      duration: 0.09,
      volume: 0.16,
    });
  }

  private playStart(): void {
    this.playTone({
      type: 'sawtooth',
      startFrequency: 240,
      endFrequency: 760,
      duration: 0.2,
      volume: 0.18,
    });
    this.playTone({
      type: 'square',
      startFrequency: 480,
      endFrequency: 920,
      duration: 0.14,
      volume: 0.08,
      delay: 0.055,
    });
  }

  private playLaneChange(direction: -1 | 1): void {
    this.playNoise({
      filterType: 'bandpass',
      startFrequency: direction === -1 ? 1_900 : 1_300,
      endFrequency: direction === -1 ? 760 : 2_300,
      duration: 0.11,
      volume: 0.13,
      pan: direction * 0.58,
    });
    this.playTone({
      type: 'triangle',
      startFrequency: direction === -1 ? 310 : 390,
      endFrequency: direction === -1 ? 230 : 520,
      duration: 0.085,
      volume: 0.07,
      pan: direction * 0.42,
    });
  }

  private playJump(): void {
    this.playTone({
      type: 'triangle',
      startFrequency: 170,
      endFrequency: 720,
      duration: 0.16,
      volume: 0.17,
    });
    this.playNoise({
      filterType: 'highpass',
      startFrequency: 420,
      endFrequency: 1_900,
      duration: 0.1,
      volume: 0.075,
    });
  }

  private playLanding(): void {
    this.playTone({
      type: 'sine',
      startFrequency: 125,
      endFrequency: 42,
      duration: 0.18,
      volume: 0.25,
    });
    this.playNoise({
      filterType: 'lowpass',
      startFrequency: 760,
      endFrequency: 170,
      duration: 0.14,
      volume: 0.15,
    });
  }

  private playSlide(): void {
    this.playNoise({
      filterType: 'bandpass',
      startFrequency: 2_400,
      endFrequency: 540,
      duration: 0.24,
      volume: 0.17,
    });
    this.playTone({
      type: 'sawtooth',
      startFrequency: 180,
      endFrequency: 82,
      duration: 0.16,
      volume: 0.075,
    });
  }

  private playCollision(): void {
    this.playTone({
      type: 'sawtooth',
      startFrequency: 105,
      endFrequency: 34,
      duration: 0.34,
      volume: 0.32,
    });
    this.playTone({
      type: 'square',
      startFrequency: 260,
      endFrequency: 62,
      duration: 0.2,
      volume: 0.12,
    });
    this.playNoise({
      filterType: 'lowpass',
      startFrequency: 2_400,
      endFrequency: 190,
      duration: 0.31,
      volume: 0.26,
    });
  }

  private playTone(options: ToneOptions): void {
    const context = this.context;
    const master = this.master;
    if (
      this.muted ||
      context === undefined ||
      master === undefined ||
      context.state !== 'running'
    ) {
      return;
    }
    const start = context.currentTime + (options.delay ?? 0);
    const end = start + options.duration;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const panner = context.createStereoPanner();
    oscillator.type = options.type;
    oscillator.frequency.setValueAtTime(options.startFrequency, start);
    oscillator.frequency.exponentialRampToValueAtTime(options.endFrequency, end);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(options.volume, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    panner.pan.value = options.pan ?? 0;
    oscillator.connect(gain);
    gain.connect(panner);
    panner.connect(master);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  }

  private playNoise(options: NoiseOptions): void {
    const context = this.context;
    const master = this.master;
    if (
      this.muted ||
      context === undefined ||
      master === undefined ||
      this.noiseBuffer === undefined ||
      context.state !== 'running'
    ) {
      return;
    }
    const start = context.currentTime;
    const end = start + options.duration;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    const panner = context.createStereoPanner();
    source.buffer = this.noiseBuffer;
    filter.type = options.filterType;
    filter.frequency.setValueAtTime(options.startFrequency, start);
    filter.frequency.exponentialRampToValueAtTime(options.endFrequency, end);
    filter.Q.value = options.filterType === 'bandpass' ? 0.8 : 0.3;
    gain.gain.setValueAtTime(options.volume, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    panner.pan.value = options.pan ?? 0;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(master);
    source.start(start);
    source.stop(end + 0.02);
  }
}
